import React, { useMemo, useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaStar } from "react-icons/fa";
import { RiDoubleQuotesL } from "react-icons/ri";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import suspa from "../../assets/whoWeAre/partners/suspa.png"
import Yuken from '../../assets/whoWeAre/partners/yuken.png'
import hycom from '../../assets/whoWeAre/partners/hycom.png'
import ucal from '../../assets/whoWeAre/partners/ucal.png'

const testimonials = [
  {
    name: "Suspa",
    role: "Pneumatics",
    image: suspa,
    text: "We value our partnership with Twin Star Industries for their consistent quality, timely delivery, and customer-focused approach. Their team has been a reliable supplier, ensuring high standards that align with our expectations.",
    rating: 5,
  },
  {
    name: "Yuken",
    role: "",
    image: Yuken,
    text: "Twin Star Industries has been a dependable supplier, maintaining precision and quality across every order. Their commitment to excellence makes them a trusted partner for our operations.",
    rating: 5,
  },
  {
    name: "Hycom",
    role: "Engineering",
    image: hycom,
    text: "Working with Twin Star Industries has been a smooth and professional experience. Their responsiveness, product reliability, and strong technical support have strengthened our long-term relationship.",
    rating: 5,
  },
  {
    name: "Ucal",
    role: "Fuel System",
    image: ucal,
    text: "Twin Star Industries has consistently delivered high-quality components with precision and reliability. Their dedication to timely supply and adherence to industry standards has made them a valuable partner in our supply chain.",
    rating: 5,
  },
];

// Animation container
const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04 },
  },
};

// Letter animation
const letter = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
};

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [animateHeading, setAnimateHeading] = useState(false);
  const sectionRef = useRef(null);

  const headingText = "What our users\n say about us.";

  // Intersection Observer for heading animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setAnimateHeading(entry.isIntersecting);
      },
      { threshold: 0.4 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const visible = useMemo(() => {
    const a = testimonials[index % testimonials.length];
    const b =
      testimonials[(index + 1) % testimonials.length] ?? testimonials[0];
    return [a, b];
  }, [index]);

  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeInOut" },
    },
    exit: (dir) => ({
      x: dir > 0 ? -100 : 100,
      opacity: 0,
      transition: { duration: 0.5, ease: "easeInOut" },
    }),
  };

  return (
    <div className="font-sans text-gray-800 bg-blue-100"> 
    <section
      ref={sectionRef}
      className="px-4 sm:px-8 md:px-12 lg:px-20 py-12 md:py-16 bg-gray-50 overflow-hidden"
    >
      {/* Top row */}
      <div className="flex flex-col md:flex-row items-start justify-between gap-6">
        <div>
          <span className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 bg-blue-500 rounded-full" />
            <span className="text-base sm:text-lg font-medium">
              Testimonials
            </span>
          </span>

          <motion.h2
            className="text-[32px] sm:text-[40px] md:text-[48px] lg:text-5xl font-semibold text-black leading-tight flex flex-wrap"
            variants={container}
            initial="hidden"
            animate={animateHeading ? "visible" : "hidden"}
          >
            {headingText.split("").map((char, i) => (
              <motion.span key={i} variants={letter}>
                {char === " " ? "\u00A0" : char === "\n" ? <br /> : char}
              </motion.span>
            ))}
          </motion.h2>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-3 mt-1">
          <button
            aria-label="Previous"
            onClick={() => {
              setDirection(-1);
              setIndex(
                (i) => (i - 1 + testimonials.length) % testimonials.length
              );
            }}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#000d2f] text-white grid place-items-center hover:opacity-90 transition"
          >
            <FiChevronLeft className="text-lg sm:text-2xl" />
          </button>
          <button
            aria-label="Next"
            onClick={() => {
              setDirection(1);
              setIndex((i) => (i + 1) % testimonials.length);
            }}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#000d2f] text-white grid place-items-center hover:opacity-90 transition"
          >
            <FiChevronRight className="text-lg sm:text-2xl" />
          </button>
        </div>
      </div>

      {/* Cards with slide animation */}
      <div className="mt-10 relative min-h-[300px]">
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={index}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 absolute w-full"
          >
            {visible.map((item, i) => (
              <div
                key={i}
                className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-sm flex flex-col justify-between"
              >
                <RiDoubleQuotesL className="text-[28px] sm:text-[32px] text-black mb-4" />
                <p className="text-base sm:text-lg font-medium text-black leading-relaxed mb-8">
                  {item.text}
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-fit"
                    />
                    <div>
                      <p className="font-bold text-black text-sm sm:text-base">
                        {item.name}
                      </p>
                      <p className="text-xs sm:text-sm text-[#4a4f54]">
                        {item.role}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-start sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center">
                      {Array.from({ length: item.rating }).map((_, s) => (
                        <FaStar key={s} className="text-yellow-400" />
                      ))}
                    </div>
                    <span className="text-xs sm:text-sm text-[#4a4f54]">
                      {item.rating} Star Rating
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div><br />
    </section>
    </div>
    
  );
}
